import { useCallback, useEffect } from 'react';
import { EditExportTemplate } from 'components/AppService/ExamV3Settings/ExportTemplates';
import LoadingWrapper from 'components/LoadingWrapper';
import dayjs from 'dayjs';
import { useTableTitle } from 'hooks/useTableTitle';
import {
  appServiceExamV3ObjectTypes,
  fetchExamV3Mapping,
} from 'slices/appService.slice';
import {
  deleteExamV3ExportTemplate,
  examV3ExportTemplateReset,
  examV3ExportTemplatesLoadingSelector,
  examV3ExportTemplatesOriginalSelectedTemplateSelector,
  examV3ExportTemplatesSelectedTemplateSelector,
  examV3ExportTemplatesSelector,
  examV3SelectExportTemplate,
  fetchExamV3ExportTemplate,
  fetchExamV3ExportTemplates,
  saveExamV3ExportTemplate,
} from 'slices/examV3ExportTemplates.slice';
import {
  examV3ReservationSettingsSelector,
  examV3SettingsLoadingSelector,
  fetchExamV3ReservationSettings,
} from 'slices/examV3Settings.slice';
import { fetchFields, fieldsSelector } from 'slices/field.slice';
import {
  fetchObjectMapping,
  requestObjectTypes,
  selectObjectTypesList,
  selectObjectTypesMap,
} from 'slices/objectTypes.slice';
import {
  fetchReservationTemplates,
  reservationTemplatesLoading,
  reservationTemplatesSelector,
} from 'slices/reservationTemplate.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { fetchTimeZones } from 'slices/timeZone.slice';
import type {
  ExamV3ExportTemplates,
  TemplateType,
} from 'types/examV3ExportTemplates.type';
import { EApplicationObjectTypeGroup, type TType } from '@timeedit/types';
import {
  Button,
  ConfirmationPopup,
  DefaultContentLayout,
  EditableTable,
  Icon,
  TEDrawer,
} from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';

const ExportTemplates = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchTimeZones());
    dispatch(fetchReservationTemplates());
    dispatch(fetchExamV3ExportTemplates());
    dispatch(fetchExamV3ReservationSettings());
    dispatch(requestObjectTypes());
    dispatch(fetchFields());
    dispatch(fetchObjectMapping());
    dispatch(fetchExamV3Mapping());
  }, [dispatch]);

  const exportTemplates = useAppSelector(examV3ExportTemplatesSelector);
  const selectedExportTemplate = useAppSelector(
    examV3ExportTemplatesSelectedTemplateSelector
  );
  const originalExportTemplate = useAppSelector(
    examV3ExportTemplatesOriginalSelectedTemplateSelector
  );
  const allTypes: TType[] = useAppSelector(selectObjectTypesList);
  const allFields = useAppSelector(fieldsSelector);
  const reservationTemplates = useAppSelector(reservationTemplatesSelector);
  const templateSettingsLoading = useAppSelector(reservationTemplatesLoading);
  const reservationSettings = useAppSelector(examV3ReservationSettingsSelector);
  const examV3Mapping = useAppSelector(appServiceExamV3ObjectTypes);
  const examV3MappingLoading = useAppSelector(examV3SettingsLoadingSelector);
  const objectMapping = useAppSelector(selectObjectTypesMap);

  const studentExamMapping = examV3Mapping.find(
    (mapping) =>
      mapping.applicationObjectTypeGroup === EApplicationObjectTypeGroup.STUDENT
  );

  const capResTemplate = reservationTemplates?.find(
    (templ) => templ.id === reservationSettings.capacityReservationTemplate
  );

  const sizeResTemplate = reservationTemplates?.find(
    (templ) => templ.id === reservationSettings.sizeReservationTemplate
  );

  const studentObjectMappingId =
    objectMapping[studentExamMapping?.objectTypeId]?.id;

  const loading = useAppSelector(examV3ExportTemplatesLoadingSelector);

  const title = useTableTitle({ total: exportTemplates.length });

  const onCreate = useCallback(() => {
    dispatch(
      examV3SelectExportTemplate({
        title: '',
        mainHeader: [],
        mainHeaderNoOfColumns: 2,
        templateType: 'room-specific',
        pageOrientation: 'portrait',
        seatingList: {
          studentList: {
            title: {
              label: 'title',
            },
            columns: [],
            header: [],
            headerNoOfColumns: 2,
          },
          examRange: {
            columns: [],
          },
        },
      })
    );
  }, [dispatch]);

  const onRow = useCallback(
    (template: ExamV3ExportTemplates) => {
      return {
        onClick: () => dispatch(fetchExamV3ExportTemplate(template._id)),
      };
    },
    [dispatch]
  );

  const footer = useCallback(
    () => (
      <Space direction="horizontal">
        <Button size="small" type="primary" onClick={onCreate}>
          Create new
        </Button>
      </Space>
    ),
    [onCreate]
  );

  const menuProps = useCallback(
    (record: ExamV3ExportTemplates) => ({
      items: [
        {
          key: 'delete',
          label: <span style={{ color: 'red' }}>Delete</span>,
          icon: (
            <Icon id="DeleteOutlined" alt="Delete" style={{ color: 'red' }} />
          ),
        },
      ],
      onClick: (info) => {
        info.domEvent.stopPropagation();
        if (info.key === 'delete') {
          dispatch(deleteExamV3ExportTemplate(record._id));
        }
      },
    }),
    [dispatch]
  );
  return (
    <>
      <DefaultContentLayout
        title={'Export templates'}
        subtitle={'Create, edit and delete export templates'}
      >
        <div className="inner-content--wrapper">
          <LoadingWrapper isLoading={loading}>
            <EditableTable<ExamV3ExportTemplates>
              rowKey="_id"
              title={title}
              onRow={onRow}
              columns={columns}
              dataSource={exportTemplates}
              pagination={false}
              size="small"
              footer={footer}
              getMenuProps={menuProps}
            />
          </LoadingWrapper>
          <TEDrawer
            expandWidth="70vw"
            firstDrawer={{
              title: originalExportTemplate?.name ?? 'New export template',
              content: (
                <EditExportTemplate
                  allFields={allFields}
                  allTypes={allTypes}
                  capResTemplate={capResTemplate}
                  sizeResTemplate={sizeResTemplate}
                  studentObjectId={studentObjectMappingId}
                  loadingConfiguration={
                    templateSettingsLoading || examV3MappingLoading
                  }
                />
              ),
              drawerOpen: !!selectedExportTemplate,
              setDrawerOpen: () => dispatch(examV3SelectExportTemplate(null)),
              extra: (
                <DrawerExtras
                  id={selectedExportTemplate?._id ?? selectedExportTemplate?.id}
                />
              ),
            }}
          />
        </div>
      </DefaultContentLayout>
    </>
  );
};

const DrawerExtras = ({ id }: { id: string | undefined }) => {
  const dispatch = useAppDispatch();
  return (
    <Space>
      <ConfirmationPopup
        message="Are you sure?"
        onConfirm={() => dispatch(deleteExamV3ExportTemplate(id))}
      >
        <Button
          disabled={id == undefined}
          style={{ marginRight: 8 }}
          size="small"
          icon={<Icon id="DeleteOutlined" alt="Delete" />}
        />
      </ConfirmationPopup>
      <Button
        size="small"
        onClick={() => dispatch(examV3ExportTemplateReset())}
      >
        Discard
      </Button>
      <Button
        size="small"
        type="primary"
        onClick={() => dispatch(saveExamV3ExportTemplate())}
      >
        Save
      </Button>
    </Space>
  );
};

const columns = [
  {
    key: 'name',
    title: 'Name',
    dataIndex: 'name',
  },
  {
    key: 'type',
    title: 'Type',
    dataIndex: 'templateType',
    render: (type: TemplateType | undefined): string => {
      const typeToString: Record<TemplateType, string> = {
        'room-specific': 'Room specific',
        'entrance-list': 'Entrance list',
        'invigilator-attendance': 'Invigilator attendance',
      };

      return typeToString[type];
    },
  },
  {
    key: 'createdAt',
    title: 'Created at',
    dataIndex: 'createdAt',
    render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
  },
  {
    key: 'updatedAt',
    title: 'Updated at',
    dataIndex: 'updatedAt',
    render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
  },
];

export default ExportTemplates;
